import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/InfoRounded'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { DashboardDetailResponse } from '@counsel-project/counsel-audit-ai-api'
import FlagRoundedIcon from '@mui/icons-material/FlagRounded'
import { alpha } from '@mui/material/styles'
import { removeMaliciousHTML } from '../../../util/markdown'
import { Divider } from '@mui/material'
import {
  MarkdownDocWithHighlights,
  markdownToHTML,
  severityAlphaColors,
  severityColors,
} from '@counsel-project/components'
import { useMemo } from 'react'

export type DocAnalysisProps = {
  markdown: string
  alerts: DashboardDetailResponse['result']['alerts']
}

type SeverityCardProps = {
  severity: 1 | 2 | 3
  alerts: DashboardDetailResponse['result']['alerts']
  onElementClick: (severity: 'low' | 'medium' | 'critical') => void
}

const SeverityCard = ({ severity, alerts, onElementClick }: SeverityCardProps) => {
  const severityConfig = {
    1: {
      icon: <InfoIcon sx={{ color: 'white', fontSize: '1.4rem' }} />,
      label: 'Low Severity',
      clickType: 'low' as const,
    },
    2: {
      icon: <ReportProblemIcon sx={{ color: 'white', fontSize: '1.4rem' }} />,
      label: 'Medium Severity',
      clickType: 'medium' as const,
    },
    3: {
      icon: <ErrorIcon sx={{ color: 'white', fontSize: '1.4rem' }} />,
      label: 'High Severity',
      clickType: 'critical' as const,
    },
  }

  const config = severityConfig[severity]

  // Calculate issue count for this severity
  const issueCount = alerts.filter((alert) => alert.severity === severity).length

  // Calculate how many severity types have issues
  const severitiesWithIssues = [1, 2, 3].filter((sev) =>
    alerts.some((alert) => alert.severity === sev)
  ).length

  // Calculate md size based on how many severity types have issues
  const calculateMdSize = () => {
    if (severitiesWithIssues === 3) return 4
    if (severitiesWithIssues === 2) return 6
    return 12
  }

  // Don't render if this severity has no issues
  if (issueCount === 0) return null

  return (
    <Grid item xs={12} md={calculateMdSize()}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: severityColors[severity],
          p: 2,
          cursor: 'pointer',
          transition: 'background-color 0.3s ease',
          '&:hover': { backgroundColor: severityAlphaColors[severity] },
        }}
        onClick={() => onElementClick(config.clickType)}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {config.icon}
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="body1" fontSize={16} fontWeight="bold" color="white">
              {`${issueCount} ${config.label}`}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  )
}

const AllAlerts = ({ alerts }: { alerts: DashboardDetailResponse['result']['alerts'] }) => {
  return (
    <Paper elevation={3} sx={{ p: 2 }} id="cnai-all-alerts">
      <Typography variant="h6" sx={{ mb: 2 }}>
        All Alerts
      </Typography>
      {alerts.map((alert, index) => {
        const parts = alert.text.split('\n...\n').filter((part) => part.trim() !== '')
        const htmlParts = parts.map((part) => removeMaliciousHTML(markdownToHTML(part)))

        return (
          <Grid item key={alert.id}>
            <Box>
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}
              >
                <FlagRoundedIcon
                  sx={{ color: severityColors[alert.severity as keyof typeof severityColors] }}
                />
                {alert.title}
              </Typography>
              <Typography variant="body1">{alert.reason}</Typography>
              {htmlParts.map((html, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: index === htmlParts.length - 1 ? '0' : '8px',
                    padding: '8px',
                    borderRadius: '8px',
                    background: `linear-gradient(to right, ${alpha(
                      severityColors[alert.severity as keyof typeof severityColors],
                      0.15
                    )}, ${alpha(
                      severityColors[alert.severity as keyof typeof severityColors],
                      0.1
                    )})`,
                  }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ))}
              {index !== alerts.length - 1 && <Divider sx={{ my: 2 }} />}
            </Box>
          </Grid>
        )
      })}
    </Paper>
  )
}

const DocAnalysis = ({ markdown, alerts }: DocAnalysisProps) => {
  const hasIssues = alerts.length > 0

  const handleElementClick = (severity: 'low' | 'medium' | 'critical') => {
    const firstIssueElement = document.getElementsByClassName(
      `cnai-rich-editor-doc-highlight-${severity}`
    )?.[0]

    if (firstIssueElement) {
      firstIssueElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

      // Open the alert
      if (firstIssueElement instanceof HTMLElement) {
        firstIssueElement.click()
      }
    } else {
      // The element is not in the document
      const allAlertsElement = document.getElementById('cnai-all-alerts')
      if (allAlertsElement) {
        allAlertsElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const nonEmptyAlerts = useMemo(
    () =>
      alerts
        .filter((alert) => alert.text.trim() !== '')
        .map((alert) => ({
          ...alert,
          severity: alert.severity as 1 | 2 | 3,
        })),
    [alerts]
  )

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 2 }}>
          {hasIssues ? (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Document Issues
              </Typography>
              <Grid container spacing={2}>
                <SeverityCard severity={1} alerts={alerts} onElementClick={handleElementClick} />
                <SeverityCard severity={2} alerts={alerts} onElementClick={handleElementClick} />
                <SeverityCard severity={3} alerts={alerts} onElementClick={handleElementClick} />
              </Grid>
            </>
          ) : (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#4caf50',
                p: 3,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleIcon sx={{ color: 'white', fontSize: '2rem' }} />
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography variant="body1" fontSize={18} fontWeight="bold" color="white">
                    No issues found
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <MarkdownDocWithHighlights value={markdown} uniqueAlerts={nonEmptyAlerts} />
      </Grid>
      {hasIssues && (
        <Grid item xs={12}>
          <AllAlerts alerts={alerts} />
        </Grid>
      )}
    </Grid>
  )
}

export default DocAnalysis
